import * as yup from 'yup';

import { ADDITIONAL_TARIFFS_VALIDATION_SCHEMA } from './AdditionalTariffValidationSchema';
import { COMPLIANCE_PRICING_VALIDATION_SCHEMA } from './CompliancePricingValidationSchema';
import { TRANSACTION_PRICING_VALIDATION } from './TripleToggleValidationSchema';

const asNum = (s: yup.NumberSchema<number | undefined>) => {
    return s.transform((v, o) => {
        if (o === '' || o == null) return undefined;
        const n = Number(o);
        return Number.isNaN(n) ? undefined : n;
    });
};

const transactionPricingShape = TRANSACTION_PRICING_VALIDATION();

export const TRANSACTION_PRICING_OFFER_VALIDATION_SCHEMA = yup.object({
    offerName: yup.string().required('Required'),

    revenueShare: asNum(
        yup
            .number()
            .min(0, 'Minimum amount of revenue shares is 0%')
            .max(100, 'Your entry exceeds 100%')
            .required('Required')
    ),

    country: yup.string().required('Required'),

    transactionPricing: yup.object(transactionPricingShape),

    additionalTariffs: ADDITIONAL_TARIFFS_VALIDATION_SCHEMA,

    compliancePricing: COMPLIANCE_PRICING_VALIDATION_SCHEMA,
});

const VALIDATION_ERROR_MESSAGES = {
    DEFAULT: 'Please fill in required fields',
    EXCEEDS_100_PERCENT: 'Your entry exceeds 100%',
    REQUIRED_FIELDS: 'Please fill in all required fields',
    HIGHEST_RATE_LESS_THAN_LOWEST: 'Highest rate cannot be less than lowest rate',
    MINIMUM_REVENUE_SHARE: 'Minimum amount of revenue shares is 0%',
} as const;

/**
 * Extracts meaningful error message from validation error
 *
 * @param error - The validation error object
 * @returns A user-friendly error message
 */
export const extractValidationErrorMessage = (error: unknown): string => {
    if (!error || typeof error !== 'object' || !('message' in error)) {
        return VALIDATION_ERROR_MESSAGES.DEFAULT;
    }

    const { message } = error as { message: unknown };

    // Ensure message is a string before using string methods
    if (typeof message !== 'string') {
        return VALIDATION_ERROR_MESSAGES.DEFAULT;
    }

    // Extract specific error messages
    if (message.includes('Your entry exceeds 100%')) {
        return VALIDATION_ERROR_MESSAGES.EXCEEDS_100_PERCENT;
    }

    if (message.includes('is a required field') || message.includes('Required')) {
        return VALIDATION_ERROR_MESSAGES.REQUIRED_FIELDS;
    }

    if (message.includes('Highest rate cannot be less than lowest rate')) {
        return VALIDATION_ERROR_MESSAGES.HIGHEST_RATE_LESS_THAN_LOWEST;
    }

    if (message.includes('Minimum amount of revenue shares is 0%')) {
        return VALIDATION_ERROR_MESSAGES.MINIMUM_REVENUE_SHARE;
    }

    return VALIDATION_ERROR_MESSAGES.DEFAULT;
};
